import React, { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { formatDate } from "@/lib/format";
import { mergeDesign } from "@/lib/design";
import { getPremiumTheme, DecorDivider, CornerOrnament } from "@/lib/premiumThemes";
import MusicPlayer from "@/components/MusicPlayer";
import ThemeDecoration from "@/components/ThemeDecoration";
import {
  ChevronDown, MailOpen, MapPin, CalendarDays,
  Copy, Heart, Instagram, Check, X,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pick = (uploaded, url) => uploaded || url || "";

// Mobile-centered invitation container width.
const PHONE_MAX_W = "max-w-[430px]";

// Countdown hook -----------------------------------------------------------
function useCountdown(dateStr) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0, done: false });
  useEffect(() => {
    if (!dateStr) return;
    const target = new Date(dateStr + "T00:00:00").getTime();
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) { setT({ d: 0, h: 0, m: 0, s: 0, done: true }); return; }
      setT({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000), done: false,
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [dateStr]);
  return t;
}

// Per-theme entrance animation variants.
const ANIMATIONS = {
  "editorial-bw":         { initial: { opacity: 0, y: 0, x: 0, scale: 1 },   animate: { opacity: 1, y: 0 },       duration: 1.0 }, // subtle fade
  "ar-rum-modern":        { initial: { opacity: 0, y: 40 },                 animate: { opacity: 1, y: 0 },       duration: 0.8 }, // slide up
  "vintage-floral":       { initial: { opacity: 0, scale: 0.94, y: 20 },    animate: { opacity: 1, scale: 1, y: 0 }, duration: 0.9 }, // gentle bloom
  "dark-luxury":          { initial: { opacity: 0, scale: 1.05, y: 24 },    animate: { opacity: 1, scale: 1, y: 0 }, duration: 0.9 }, // luxury zoom
  "pastel-watercolor":    { initial: { opacity: 0, y: 24, filter: "blur(6px)" }, animate: { opacity: 1, y: 0, filter: "blur(0px)" }, duration: 1.1 }, // watercolor bloom
  "botanical-green":      { initial: { opacity: 0, x: -30 },                animate: { opacity: 1, x: 0 },       duration: 0.85 }, // side reveal
  "minimal-contemporary": { initial: { opacity: 0, y: 16 },                 animate: { opacity: 1, y: 0 },       duration: 0.7 }, // clean fade-up
};

const ThemeAnimCtx = React.createContext("editorial-bw");

function Reveal({ children, delay = 0, once = true }) {
  const themeId = React.useContext(ThemeAnimCtx);
  const a = ANIMATIONS[themeId] || ANIMATIONS["editorial-bw"];
  return (
    <motion.div
      initial={a.initial}
      whileInView={a.animate}
      viewport={{ once, margin: "-60px" }}
      transition={{ duration: a.duration, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

/** Wraps a section with optional photo background overlay. */
function SectionBg({ photo, theme, children, className = "", testid, id }) {
  const pal = theme.palette;
  if (!photo) {
    return <section id={id} data-testid={testid} className={className}>{children}</section>;
  }
  return (
    <section id={id} data-testid={testid} className={`relative overflow-hidden ${className}`}>
      <img src={photo} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover"
        style={{ opacity: theme.mode === "light" ? 0.28 : 0.4,
                 filter: theme.decor === "minimal-line" ? "grayscale(1) contrast(1.05)" : "none" }} />
      <div className="pointer-events-none absolute inset-0"
        style={{ background: `linear-gradient(180deg, ${pal.bg}b0, ${pal.bg}e0 45%, ${pal.bg}f6)` }} />
      <div className="relative z-10">{children}</div>
    </section>
  );
}

function CountBox({ value, label, pal }) {
  return (
    <div className="flex flex-col items-center">
      <div
        className="min-w-[58px] rounded-lg border px-2 py-2 backdrop-blur"
        style={{ borderColor: `${pal.accent}55`, background: pal.mode === "light" ? "rgba(0,0,0,0.04)" : "rgba(0,0,0,0.35)" }}
      >
        <span className="font-serif text-3xl font-light tabular-nums" style={{ color: pal.cream }}>
          {String(value).padStart(2, "0")}
        </span>
      </div>
      <span className="mt-2 text-[9px] uppercase tracking-[0.3em]" style={{ color: pal.dim }}>{label}</span>
    </div>
  );
}

function EventCard({ ev, delay, theme }) {
  const pal = theme.palette;
  if (!ev || (!ev.nama && !ev.tempat && !ev.tanggal)) return null;
  return (
    <Reveal delay={delay}>
      <div
        className="relative overflow-hidden rounded-2xl border p-6 text-center backdrop-blur"
        style={{ borderColor: `${pal.accent}44`, background: pal.mode === "light" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.35)" }}
      >
        <CalendarDays className="mx-auto h-6 w-6" style={{ color: pal.accent }} />
        <p className="mt-2 font-serif text-2xl italic" style={{ color: pal.cream, fontFamily: theme.fonts.serif }}>{ev.nama || "Acara"}</p>
        <DecorDivider theme={theme} w={140} />
        {ev.tanggal && <p className="text-sm" style={{ color: pal.dim }}>{formatDate(ev.tanggal)}</p>}
        {ev.jam && <p className="mt-1 text-sm" style={{ color: pal.dim }}>Pukul {ev.jam} WIB</p>}
        {ev.tempat && <p className="mt-3 text-base" style={{ color: pal.cream }}>{ev.tempat}</p>}
        {ev.alamat && <p className="mt-1 text-xs" style={{ color: pal.dim }}>{ev.alamat}</p>}
        {ev.dresscode && <p className="mt-2 text-[10px] uppercase tracking-[0.3em]" style={{ color: pal.accent }}>Dress Code: {ev.dresscode}</p>}
        {ev.maps_url && (
          <a href={ev.maps_url} target="_blank" rel="noreferrer"
            className="mt-4 inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-[10px] uppercase tracking-[0.3em] transition-opacity hover:opacity-80"
            style={{ borderColor: pal.accent, color: pal.accent }}>
            <MapPin className="h-3.5 w-3.5" /> Lokasi
          </a>
        )}
      </div>
    </Reveal>
  );
}

function PersonBlock({ label, data, theme }) {
  const pal = theme.palette;
  const src = pick(data.foto, data.foto_url);
  if (!data.nama_lengkap && !src) return null;
  const shape = theme.decor === "geometric" ? "rounded-2xl" : "rounded-full";
  return (
    <div className="text-center">
      {src && (
        <div
          className={`mx-auto mb-4 h-40 w-40 overflow-hidden ${shape}`}
          style={{
            boxShadow: theme.decor === "art-deco"
              ? `0 0 0 1px ${pal.accent}, 0 0 0 6px ${pal.bg}, 0 0 0 7px ${pal.accent}`
              : `0 0 0 1px ${pal.accent}55, 0 0 0 5px ${pal.bg}, 0 0 0 6px ${pal.accent2}55`,
          }}
        >
          <img src={src} alt={data.nama_lengkap} className="h-full w-full object-cover" />
        </div>
      )}
      <p className="text-[10px] uppercase tracking-[0.4em]" style={{ color: pal.accent }}>{label}</p>
      {data.nama_lengkap && (
        <h3 className="mt-2 text-3xl italic"
          style={{ fontFamily: theme.fonts.script, color: pal.cream }}>
          {data.nama_lengkap}
        </h3>
      )}
      {data.bio && <p className="mt-3 text-sm" style={{ color: pal.dim }}>{data.bio}</p>}
      {(data.ayah || data.ibu) && (
        <p className="mt-3 text-sm leading-relaxed" style={{ color: pal.dim }}>
          Putra/Putri dari<br />
          {data.ayah && <>Bapak <b style={{ color: pal.cream }}>{data.ayah}</b><br /></>}
          {data.ibu && <>Ibu <b style={{ color: pal.cream }}>{data.ibu}</b></>}
        </p>
      )}
      {data.instagram && (
        <a href={`https://instagram.com/${data.instagram}`} target="_blank" rel="noreferrer"
          className="mt-4 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] transition-opacity hover:opacity-80"
          style={{ borderColor: `${pal.accent}88`, color: pal.accent }}>
          <Instagram className="h-3 w-3" /> @{data.instagram}
        </a>
      )}
    </div>
  );
}

function GiftCard({ acc, theme }) {
  const pal = theme.palette;
  const copy = async () => {
    try { await navigator.clipboard.writeText(acc.nomor); toast.success("Nomor rekening disalin"); }
    catch { toast.info(acc.nomor); }
  };
  return (
    <div
      className="relative overflow-hidden rounded-2xl p-5 text-left"
      style={{
        background: pal.mode === "light" ? pal.secondary : `linear-gradient(135deg, ${pal.secondary}, ${pal.bg})`,
        border: `1px solid ${pal.accent}44`,
      }}
    >
      <p className="text-[10px] uppercase tracking-[0.3em]" style={{ color: pal.accent }}>{acc.label || "Bank"}</p>
      {acc.nomor && (
        <p className="mt-3 select-all font-mono text-xl tabular-nums" style={{ color: pal.cream }}>{acc.nomor}</p>
      )}
      {acc.atas_nama && <p className="mt-1 text-xs" style={{ color: pal.dim }}>a.n. {acc.atas_nama}</p>}
      {acc.nomor && (
        <button onClick={copy}
          className="mt-3 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] transition-opacity hover:opacity-80"
          style={{ borderColor: pal.accent, color: pal.accent }}>
          <Copy className="h-3 w-3" /> Salin
        </button>
      )}
    </div>
  );
}

function Lightbox({ src, onClose }) {
  if (!src) return null;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 p-6"
      data-testid="lightbox">
      <button onClick={onClose} className="absolute right-6 top-6 rounded-full bg-white/10 p-2 text-white">
        <X className="h-5 w-5" />
      </button>
      <motion.img initial={{ scale: 0.9 }} animate={{ scale: 1 }} src={src} alt=""
        className="max-h-[90vh] max-w-full rounded-xl object-contain" onClick={(e) => e.stopPropagation()} />
    </motion.div>
  );
}

function Gallery({ items, onOpen }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="columns-2 gap-2 [&>*]:mb-2">
      {items.map((g, i) => (
        <motion.img key={i} src={g} alt={`galeri-${i}`} loading="lazy"
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.5, delay: (i % 6) * 0.06 }}
          onClick={() => onOpen(g)}
          className="w-full cursor-zoom-in break-inside-avoid rounded-lg object-cover shadow-lg transition-transform hover:scale-[1.02]" />
      ))}
    </div>
  );
}

function RSVPForm({ pid, cfg, events, theme }) {
  const pal = theme.palette;
  const [f, setF] = useState({
    nama: "", tamu_dari: "Pria", keterangan: "Teman",
    kehadiran: "Hadir", jumlah: 1, pesan: "", acara: events[0]?.nama || "",
  });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [c1] = useState(() => Math.floor(Math.random() * 8) + 1);
  const [c2] = useState(() => Math.floor(Math.random() * 8) + 1);
  const [cap, setCap] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const showAcara = cfg.field_acara && events.length > 1;
  const bg = pal.mode === "light" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.4)";
  const optBg = pal.mode === "light" ? "#ffffff" : "#111111";
  const inputCls = "w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none";
  const style = { borderColor: `${pal.accent}55`, color: pal.cream, background: pal.mode === "light" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.3)" };

  const submit = async (e) => {
    e.preventDefault();
    if (cfg.field_nama && !f.nama.trim()) { toast.error("Isi nama"); return; }
    if (parseInt(cap, 10) !== c1 + c2) { toast.error("Jawaban verifikasi salah"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/public/rsvp/${pid}`, {
        ...f, jumlah: parseInt(f.jumlah, 10) || 1,
        pesan: showAcara ? `[${f.acara}] ${f.pesan}` : f.pesan,
      });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.status === 429 ? "Terlalu banyak percobaan." : "Gagal mengirim.");
      setLoading(false);
    }
  };

  if (sent) return (
    <div className="rounded-2xl border p-6 text-center backdrop-blur" style={{ borderColor: `${pal.accent}55`, background: bg }}>
      <Check className="mx-auto h-8 w-8" style={{ color: pal.accent }} />
      <p className="mt-3 font-serif text-xl italic" style={{ color: pal.cream, fontFamily: theme.fonts.script }}>
        Terima kasih, {f.nama}!
      </p>
      <p className="mt-1 text-sm" style={{ color: pal.dim }}>Konfirmasi kehadiran diterima.</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="rounded-2xl border p-5 backdrop-blur"
      style={{ borderColor: `${pal.accent}55`, background: bg }} data-testid="premium-rsvp-form">
      <div className="space-y-3">
        {cfg.field_nama && (
          <input data-testid="premium-rsvp-nama" className={inputCls} style={style}
            placeholder="Nama Anda" value={f.nama} onChange={(e) => set("nama", e.target.value)} />
        )}
        <div className="grid grid-cols-2 gap-3">
          <select className={inputCls} style={style} value={f.tamu_dari} onChange={(e) => set("tamu_dari", e.target.value)}>
            <option style={{ background: optBg }} value="Pria">Tamu Pria</option>
            <option style={{ background: optBg }} value="Wanita">Tamu Wanita</option>
          </select>
          <select className={inputCls} style={style} value={f.keterangan} onChange={(e) => set("keterangan", e.target.value)}>
            {["Teman", "Kolega", "Kerabat Dekat", "Keluarga", "Lainnya"].map((k) => (
              <option key={k} value={k} style={{ background: optBg }}>{k}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <select className={inputCls} style={style} value={f.kehadiran} onChange={(e) => set("kehadiran", e.target.value)}>
            <option style={{ background: optBg }} value="Hadir">Hadir</option>
            <option style={{ background: optBg }} value="Tidak Hadir">Tidak Hadir</option>
          </select>
          <input className={inputCls} style={style} type="number" min="1" value={f.jumlah}
            onChange={(e) => set("jumlah", e.target.value)} placeholder="Jumlah" />
        </div>
        {showAcara && (
          <select className={inputCls} style={style} value={f.acara} onChange={(e) => set("acara", e.target.value)}>
            {events.map((ev, i) => (
              <option key={i} value={ev.nama || `Acara ${i + 1}`} style={{ background: optBg }}>
                {ev.nama || `Acara ${i + 1}`}
              </option>
            ))}
          </select>
        )}
        {cfg.field_pesan && (
          <textarea className={inputCls} style={style} rows={3} placeholder="Pesan / doa"
            value={f.pesan} onChange={(e) => set("pesan", e.target.value)} />
        )}
        <input className={inputCls} style={style} inputMode="numeric"
          placeholder={`Verifikasi: ${c1} + ${c2}?`} value={cap} onChange={(e) => setCap(e.target.value)} />
        <button type="submit" disabled={loading}
          className="w-full rounded-full py-3 text-sm font-semibold uppercase tracking-[0.3em] transition-transform hover:-translate-y-0.5"
          style={{ background: pal.accent, color: pal.mode === "light" ? "#ffffff" : pal.bg }}>
          {loading ? "Mengirim..." : (cfg.cta_label || "Konfirmasi Kehadiran")}
        </button>
      </div>
    </form>
  );
}

function Guestbook({ pid, theme }) {
  const pal = theme.palette;
  const [nama, setNama] = useState("");
  const [pesan, setPesan] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [c1] = useState(() => Math.floor(Math.random() * 8) + 1);
  const [c2] = useState(() => Math.floor(Math.random() * 8) + 1);
  const [cap, setCap] = useState("");
  const load = async () => {
    try { const r = await axios.get(`${API}/public/guestbook/${pid}`); setList(r.data || []); }
    catch { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);
  const bg = pal.mode === "light" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.4)";
  const inputCls = "w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none";
  const style = { borderColor: `${pal.accent}55`, color: pal.cream, background: pal.mode === "light" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.3)" };
  const submit = async (e) => {
    e.preventDefault();
    if (!nama.trim() || !pesan.trim()) { toast.error("Nama dan pesan wajib diisi"); return; }
    if (parseInt(cap, 10) !== c1 + c2) { toast.error("Verifikasi salah"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/public/guestbook/${pid}`, { nama, pesan });
      setNama(""); setPesan(""); setCap(""); toast.success("Ucapan terkirim"); load();
    } catch { toast.error("Gagal mengirim ucapan"); }
    finally { setLoading(false); }
  };
  return (
    <div>
      <form onSubmit={submit} className="rounded-2xl border p-5 backdrop-blur" style={{ borderColor: `${pal.accent}55`, background: bg }}>
        <div className="space-y-3">
          <input className={inputCls} style={style} placeholder="Nama" value={nama} onChange={(e) => setNama(e.target.value)} />
          <textarea className={inputCls} style={style} rows={3} placeholder="Ucapan dan doa..." value={pesan} onChange={(e) => setPesan(e.target.value)} />
          <input className={inputCls} style={style} inputMode="numeric" placeholder={`Verifikasi: ${c1} + ${c2}?`} value={cap} onChange={(e) => setCap(e.target.value)} />
          <button type="submit" disabled={loading}
            className="w-full rounded-full py-3 text-sm font-semibold uppercase tracking-[0.3em] transition-transform hover:-translate-y-0.5"
            style={{ background: pal.accent, color: pal.mode === "light" ? "#ffffff" : pal.bg }}>
            {loading ? "Mengirim..." : "Kirim Ucapan"}
          </button>
        </div>
      </form>
      {list.length > 0 && (
        <div className="mt-5 max-h-[380px] space-y-3 overflow-y-auto pr-1" data-testid="premium-guestbook-wall">
          {list.map((g, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.03 }}
              className="rounded-xl border p-4"
              style={{ borderColor: `${pal.accent}22`, background: pal.mode === "light" ? "rgba(0,0,0,0.03)" : "rgba(255,255,255,0.03)" }}>
              <p className="text-sm leading-relaxed italic" style={{ color: pal.cream, fontFamily: theme.fonts.serif }}>"{g.pesan}"</p>
              <p className="mt-2 text-[10px] uppercase tracking-[0.3em]" style={{ color: pal.accent }}>— {g.nama}</p>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// Hero variants — pick style per theme.
// -----------------------------------------------------------------------
function Hero({ theme, titleNames, weddingDate, coverPhoto, design }) {
  const pal = theme.palette;
  const acc = pal.accent;

  const commonNames = (
    <h1 className="text-5xl leading-none sm:text-6xl"
      style={{ fontFamily: theme.fonts.script, color: pal.cream }}>
      {titleNames}
    </h1>
  );

  // 1. Editorial B&W — full-width photo, serif overlay
  if (theme.hero === "editorial-overlay") {
    return (
      <section className="relative min-h-[95vh] w-full overflow-hidden">
        {coverPhoto && (
          <img src={coverPhoto} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ filter: "grayscale(1) contrast(1.05)" }} />
        )}
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, #ffffff40 0%, #ffffff20 40%, #ffffffcc 100%)" }} />
        <div className="relative z-10 flex min-h-[95vh] flex-col items-center justify-center px-6 text-center">
          <p className="text-[10px] uppercase tracking-[0.6em]" style={{ color: acc }}>{design.cover.eyebrow_top || "The Wedding Of"}</p>
          <div className="my-6 h-px w-16" style={{ background: acc }} />
          <h1 className="max-w-full text-4xl font-light leading-tight sm:text-5xl" style={{ fontFamily: theme.fonts.serif, color: pal.cream, letterSpacing: "0.02em" }}>
            {titleNames}
          </h1>
          {weddingDate && (
            <p className="mt-6 text-[10px] uppercase tracking-[0.5em]" style={{ color: pal.dim }}>{formatDate(weddingDate)}</p>
          )}
        </div>
      </section>
    );
  }

  // 2. Ar-Rum verse over hero
  if (theme.hero === "verse-photo") {
    return (
      <section className="relative min-h-[100vh] w-full overflow-hidden">
        {coverPhoto && (
          <img src={coverPhoto} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ opacity: 0.6 }} />
        )}
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${pal.bg}dd 0%, ${pal.bg}55 45%, ${pal.bg}f0 100%)` }} />
        <div className="relative z-10 flex min-h-[100vh] flex-col items-center justify-center px-6 text-center">
          <p className="text-[10px] uppercase tracking-[0.5em]" style={{ color: acc }}>Q.S. Ar-Rum : 21</p>
          <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed italic"
            style={{ color: pal.cream, fontFamily: theme.fonts.serif }}>
            "Dan di antara tanda-tanda kekuasaan-Nya ialah Dia menciptakan untukmu istri-istri dari jenismu sendiri, supaya kamu cenderung dan merasa tenteram kepadanya, dan dijadikan-Nya di antaramu rasa kasih dan sayang."
          </p>
          <DecorDivider theme={theme} w={160} />
          <p className="text-[10px] uppercase tracking-[0.5em]" style={{ color: acc }}>{design.cover.eyebrow_top || "The Wedding Of"}</p>
          <div className="mt-4">{commonNames}</div>
          {weddingDate && (
            <p className="mt-6 text-[10px] uppercase tracking-[0.4em]" style={{ color: pal.dim }}>{formatDate(weddingDate)}</p>
          )}
        </div>
      </section>
    );
  }

  // 3. Floral vintage frame
  if (theme.hero === "floral-frame") {
    return (
      <section className="relative min-h-[100vh] w-full overflow-hidden">
        <div className="absolute inset-0" style={{ background: pal.bg }} />
        <CornerOrnament theme={theme} corner="tl" />
        <CornerOrnament theme={theme} corner="tr" />
        <CornerOrnament theme={theme} corner="bl" />
        <CornerOrnament theme={theme} corner="br" />
        <div className="relative z-10 flex min-h-[100vh] flex-col items-center justify-center px-6 text-center">
          {coverPhoto && (
            <div className="mx-auto h-56 w-44 overflow-hidden rounded-t-full border-2" style={{ borderColor: acc }}>
              <img src={coverPhoto} alt="" className="h-full w-full object-cover" />
            </div>
          )}
          <DecorDivider theme={theme} w={160} />
          <p className="text-[10px] uppercase tracking-[0.5em]" style={{ color: acc }}>{design.cover.eyebrow_top || "The Wedding Of"}</p>
          <div className="mt-3">{commonNames}</div>
          {weddingDate && (
            <p className="mt-4 text-[10px] uppercase tracking-[0.4em]" style={{ color: pal.dim }}>{formatDate(weddingDate)}</p>
          )}
        </div>
      </section>
    );
  }

  // 4. Art deco black+gold
  if (theme.hero === "art-deco") {
    return (
      <section className="relative min-h-[100vh] w-full overflow-hidden">
        {coverPhoto && (
          <img src={coverPhoto} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ opacity: 0.4 }} />
        )}
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${pal.bg}ee 0%, ${pal.bg}88 40%, ${pal.bg}f5 100%)` }} />
        <CornerOrnament theme={theme} corner="tl" />
        <CornerOrnament theme={theme} corner="tr" />
        <CornerOrnament theme={theme} corner="bl" />
        <CornerOrnament theme={theme} corner="br" />
        <div className="relative z-10 flex min-h-[100vh] flex-col items-center justify-center px-6 text-center">
          <div className="rounded-lg border-2 px-8 py-8" style={{ borderColor: acc }}>
            <p className="text-[10px] uppercase tracking-[0.6em]" style={{ color: acc }}>{design.cover.eyebrow_top || "The Wedding Of"}</p>
            <DecorDivider theme={theme} w={160} />
            {commonNames}
            <DecorDivider theme={theme} w={160} />
            {weddingDate && (
              <p className="text-[10px] uppercase tracking-[0.4em]" style={{ color: pal.dim }}>{formatDate(weddingDate)}</p>
            )}
          </div>
        </div>
      </section>
    );
  }

  // 5. Pastel watercolor
  if (theme.hero === "pastel-wash") {
    return (
      <section className="relative min-h-[100vh] w-full overflow-hidden">
        <div className="absolute inset-0"
          style={{ background: `radial-gradient(75% 60% at 50% 30%, ${pal.accent}66 0%, transparent 65%), radial-gradient(60% 50% at 20% 80%, ${pal.accent2}66 0%, transparent 60%), ${pal.bg}` }} />
        <div className="relative z-10 flex min-h-[100vh] flex-col items-center justify-center px-6 text-center">
          {coverPhoto && (
            <div className="mb-8 h-48 w-48 overflow-hidden rounded-full border-2" style={{ borderColor: acc }}>
              <img src={coverPhoto} alt="" className="h-full w-full object-cover" />
            </div>
          )}
          <p className="text-[10px] uppercase tracking-[0.5em]" style={{ color: acc }}>{design.cover.eyebrow_top || "The Wedding Of"}</p>
          <div className="mt-3">{commonNames}</div>
          {weddingDate && (
            <p className="mt-6 text-[10px] uppercase tracking-[0.4em]" style={{ color: pal.dim }}>{formatDate(weddingDate)}</p>
          )}
          <DecorDivider theme={theme} w={160} />
        </div>
      </section>
    );
  }

  // 6. Botanical
  if (theme.hero === "botanical-leaf") {
    return (
      <section className="relative min-h-[100vh] w-full overflow-hidden">
        <div className="absolute inset-0" style={{ background: pal.bg }} />
        <CornerOrnament theme={theme} corner="tl" />
        <CornerOrnament theme={theme} corner="tr" />
        <CornerOrnament theme={theme} corner="bl" />
        <CornerOrnament theme={theme} corner="br" />
        <div className="relative z-10 flex min-h-[100vh] flex-col items-center justify-center px-6 text-center">
          {coverPhoto && (
            <div className="h-52 w-40 overflow-hidden rounded-t-full border-2" style={{ borderColor: acc }}>
              <img src={coverPhoto} alt="" className="h-full w-full object-cover" />
            </div>
          )}
          <DecorDivider theme={theme} w={180} />
          <p className="text-[10px] uppercase tracking-[0.5em]" style={{ color: acc }}>{design.cover.eyebrow_top || "The Wedding Of"}</p>
          <div className="mt-3">{commonNames}</div>
          {weddingDate && (
            <p className="mt-4 text-[10px] uppercase tracking-[0.4em]" style={{ color: pal.dim }}>{formatDate(weddingDate)}</p>
          )}
        </div>
      </section>
    );
  }

  // 7. Minimal contemporary
  return (
    <section className="relative min-h-[100vh] w-full overflow-hidden">
      <div className="absolute inset-0" style={{ background: pal.bg }} />
      <div className="relative z-10 flex min-h-[100vh] flex-col items-center justify-center px-6 text-center">
        {coverPhoto && (
          <div className="mb-8 h-72 w-56 overflow-hidden rounded-md" style={{ boxShadow: `4px 4px 0 0 ${acc}` }}>
            <img src={coverPhoto} alt="" className="h-full w-full object-cover" />
          </div>
        )}
        <p className="text-[10px] font-semibold uppercase tracking-[0.5em]" style={{ color: acc, fontFamily: theme.fonts.sans }}>
          {design.cover.eyebrow_top || "The Wedding Of"}
        </p>
        <h1 className="mt-4 text-3xl font-light tracking-tight sm:text-4xl"
          style={{ fontFamily: theme.fonts.sans, color: pal.cream }}>
          {titleNames}
        </h1>
        <div className="mt-6 h-px w-16" style={{ background: acc }} />
        {weddingDate && (
          <p className="mt-6 text-xs uppercase tracking-[0.4em]" style={{ color: pal.dim, fontFamily: theme.fonts.sans }}>
            {formatDate(weddingDate)}
          </p>
        )}
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------
export default function InvitationPremium() {
  const { pid } = useParams();
  const [sp] = useSearchParams();
  const to = (sp.get("to") || "").slice(0, 60);
  const themeParam = sp.get("t") || "";
  const [inv, setInv] = useState(undefined);
  const [opened, setOpened] = useState(false);
  const [box, setBox] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/invitation/${pid}`).then((r) => setInv(r.data)).catch(() => setInv(null));
  }, [pid]);

  const design = mergeDesign(inv?.design);
  const cd = useCountdown(inv?.wedding_date);
  const themeId = themeParam || inv?.premium_template || "editorial-bw";
  const theme = getPremiumTheme(themeId);
  const PAL = { ...theme.palette, mode: theme.mode };
  const scriptFont = theme.fonts.script;
  const serifFont = theme.fonts.serif;

  useEffect(() => {
    const id = `premium-fonts-${theme.id}`;
    if (document.getElementById(id)) return;
    const l = document.createElement("link");
    l.id = id; l.rel = "stylesheet";
    l.href = `https://fonts.googleapis.com/css2?family=${theme.googleFonts}&display=swap`;
    document.head.appendChild(l);
    if (!document.getElementById("premium-fonts")) {
      const fb = document.createElement("link");
      fb.id = "premium-fonts"; fb.rel = "stylesheet";
      fb.href = l.href; document.head.appendChild(fb);
    }
  }, [theme.id, theme.googleFonts]);

  const openInvitation = () => {
    setOpened(true);
    setTimeout(() => document.getElementById("premium-content")?.scrollIntoView({ behavior: "smooth" }), 400);
  };

  if (inv === undefined) return (
    <div className="flex min-h-screen items-center justify-center" style={{ background: PAL.bg, color: PAL.cream, fontFamily: serifFont }}>
      <p className="text-2xl italic">Memuat undangan…</p>
    </div>
  );
  if (inv === null) return (
    <div className="flex min-h-screen items-center justify-center" style={{ background: PAL.bg, color: PAL.cream }}>
      <p className="text-xl">Undangan tidak ditemukan</p>
    </div>
  );

  const events = (inv.events && inv.events.length ? inv.events : [{
    nama: "Resepsi", tanggal: inv.wedding_date, jam: inv.wedding_time,
    tempat: inv.venue_name, alamat: inv.venue_address, maps_url: inv.maps_url,
  }]);
  const coverPhoto = pick(design.cover.photo, design.cover.photo_url) || inv.couple_photo;
  const namaPria = design.cover.nama_pria;
  const namaWanita = design.cover.nama_wanita;
  const titleNames = (namaPria && namaWanita) ? `${namaPria} & ${namaWanita}` : inv.nama;
  const showTo = to || design.cover.default_to;

  // Whole invitation is mobile-centered: everything wrapped in a max-w-[430px] column.
  return (
    <div
      className="relative min-h-screen w-full"
      style={{ background: theme.mode === "light" ? "#e8e5df" : "#050403" }}
      data-testid="premium-invitation"
      data-theme={theme.id}
    >
      {/* Outer background pattern (visible on desktop around the phone-width card) */}
      <div className="pointer-events-none fixed inset-0"
        style={{
          background: theme.mode === "light"
            ? "radial-gradient(60% 40% at 50% 20%, #ffffff 0%, transparent 60%), linear-gradient(180deg, #e8e5df, #d5cfc0)"
            : "radial-gradient(50% 30% at 50% 20%, #1a1108 0%, transparent 60%), linear-gradient(180deg, #050403, #000)",
        }}
      />

      <div className={`relative mx-auto w-full ${PHONE_MAX_W} shadow-2xl`}
        style={{ background: PAL.bg, color: PAL.cream, fontFamily: serifFont, minHeight: "100vh" }}>

        {/* Music player (auto-play on mount when opened) */}
        {opened && inv.music_url && (
          <MusicPlayer
            url={inv.music_url}
            startSeconds={inv.music_start_seconds || 0}
            color={PAL.accent}
            bg={PAL.mode === "light" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.55)"}
            testid="premium-music-player"
          />
        )}

        <ThemeAnimCtx.Provider value={theme.id}>
        {/* ENVELOPE / COVER */}
        <AnimatePresence>
          {!opened && (
            <motion.div key="cover" initial={{ opacity: 1 }} exit={{ opacity: 0, scale: 1.05 }} transition={{ duration: 0.9 }}
              className={`fixed inset-x-0 top-0 z-50 mx-auto w-full ${PHONE_MAX_W} overflow-hidden text-center`}
              style={{ height: "100vh", background: PAL.bg }}
              data-testid="premium-cover">
              {coverPhoto && (
                <motion.img initial={{ scale: 1.2 }} animate={{ scale: 1 }}
                  transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }}
                  src={coverPhoto} alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                  style={{ opacity: theme.hero === "editorial-overlay" ? 0.85 : 0.55, filter: theme.hero === "editorial-overlay" ? "grayscale(1) contrast(1.05)" : "none" }} />
              )}
              <div className="absolute inset-0"
                style={{ background: `linear-gradient(to bottom, ${PAL.bg}80, ${PAL.bg}c0, ${PAL.bg}f5)` }} />
              <div className="relative z-10 flex h-full flex-col items-center justify-center px-6">
                <p className="text-[10px] uppercase tracking-[0.6em]" style={{ color: PAL.accent }}>{design.cover.eyebrow_top || "The Wedding Of"}</p>
                <DecorDivider theme={theme} w={180} />
                <h1 className="text-5xl leading-none sm:text-6xl"
                  style={{ fontFamily: theme.hero === "minimal-contemporary" || theme.hero === "editorial-overlay" ? theme.fonts.serif : scriptFont, color: PAL.cream }}>
                  {titleNames}
                </h1>
                {design.cover.eyebrow_bottom && (
                  <p className="mt-4 text-xs uppercase tracking-[0.4em]" style={{ color: PAL.dim }}>{design.cover.eyebrow_bottom}</p>
                )}
                {inv.wedding_date && (
                  <p className="mt-1 text-xs uppercase tracking-[0.35em]" style={{ color: PAL.dim }}>{formatDate(inv.wedding_date)}</p>
                )}
                {showTo && (
                  <div className="mt-8 inline-block rounded-full border px-5 py-2 backdrop-blur"
                    style={{ borderColor: `${PAL.accent}66` }}>
                    <span className="text-[9px] uppercase tracking-[0.3em]" style={{ color: PAL.dim }}>Kepada Yth.</span>
                    <p className="mt-1 text-base" style={{ fontFamily: scriptFont, color: PAL.accent }}>{showTo}</p>
                  </div>
                )}
                <button data-testid="premium-open-invitation" onClick={openInvitation}
                  className="mt-8 inline-flex items-center gap-2 rounded-full px-6 py-3 text-[10px] font-semibold uppercase tracking-[0.4em] transition-all hover:-translate-y-1 active:scale-95"
                  style={{ background: PAL.accent, color: PAL.mode === "light" ? "#ffffff" : PAL.bg }}>
                  <MailOpen className="h-4 w-4" /> {design.cover.button_label || "Buka Undangan"}
                </button>
                <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 2, repeat: Infinity }} className="mt-10">
                  <ChevronDown className="mx-auto h-5 w-5" style={{ color: PAL.accent }} />
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* legacy music toggle removed — MusicPlayer handles play/pause */}

        {/* CONTENT */}
        <div id="premium-content" className="relative">
          {/* Animated themed decorations (parallax + reveal). Sits above bg, below content. */}
          <div className="pointer-events-none absolute inset-0 z-0">
            <ThemeDecoration theme={theme} />
          </div>
          <div className="relative z-10">
          <Hero theme={theme} titleNames={titleNames} weddingDate={inv.wedding_date} coverPhoto={coverPhoto} design={design} />

          {/* PEMBUKA */}
          {design.pembuka.enabled && (design.pembuka.quote || design.pembuka.intro) && (
            <SectionBg className="px-6 py-16" testid="premium-pembuka" theme={theme} photo={pick(design.pembuka.photo, design.pembuka.photo_url) || pick(design.pembuka.bg, design.pembuka.bg_url)}>
              <Reveal>
                <div className="text-center">
                  <DecorDivider theme={theme} w={180} />
                  {design.pembuka.text && (
                    <p className="text-[10px] uppercase tracking-[0.4em]" style={{ color: PAL.accent }}>{design.pembuka.text}</p>
                  )}
                  {design.pembuka.quote && (
                    <p className="mt-5 text-base leading-relaxed italic"
                      style={{ color: PAL.cream, fontFamily: theme.fonts.serif }}>
                      "{design.pembuka.quote}"
                    </p>
                  )}
                  {design.pembuka.quote_source && (
                    <p className="mt-3 text-[10px] uppercase tracking-[0.3em]" style={{ color: PAL.accent }}>— {design.pembuka.quote_source}</p>
                  )}
                  {design.pembuka.intro && (
                    <p className="mt-6 text-sm leading-relaxed" style={{ color: PAL.dim }}>{design.pembuka.intro}</p>
                  )}
                </div>
              </Reveal>
            </SectionBg>
          )}

          {/* MEMPELAI */}
          {(design.pria.nama_lengkap || design.wanita.nama_lengkap) && (
            <section className="px-6 py-16" data-testid="premium-mempelai">
              <Reveal>
                <h2 className="mb-10 text-center text-4xl"
                  style={{ fontFamily: scriptFont, color: PAL.cream }}>
                  Mempelai
                </h2>
              </Reveal>
              <div className="space-y-12">
                <Reveal><PersonBlock label="Mempelai Pria" data={design.pria} theme={theme} /></Reveal>
                <Reveal delay={0.15}>
                  <div className="text-center">
                    <span className="text-5xl" style={{ fontFamily: scriptFont, color: PAL.accent }}>&amp;</span>
                  </div>
                </Reveal>
                <Reveal delay={0.25}><PersonBlock label="Mempelai Wanita" data={design.wanita} theme={theme} /></Reveal>
              </div>
            </section>
          )}

          {/* COUNTDOWN */}
          {design.countdown.enabled && (
            <SectionBg className="px-6 py-16 text-center" testid="premium-countdown" theme={theme} photo={pick(design.countdown.photo, design.countdown.photo_url)}>
              <Reveal>
                <DecorDivider theme={theme} w={140} />
                <h2 className="text-3xl italic"
                  style={{ fontFamily: scriptFont, color: PAL.cream }}>
                  {design.countdown.label_atas || "Menuju Hari Bahagia"}
                </h2>
                <div className="mx-auto mt-8 flex items-center justify-center gap-2">
                  {inv.wedding_date ? (
                    cd.done ? <p className="text-2xl italic" style={{ fontFamily: scriptFont, color: PAL.accent }}>Hari yang dinanti telah tiba!</p> : (
                      <>
                        <CountBox value={cd.d} label="Hari" pal={PAL} />
                        <CountBox value={cd.h} label="Jam" pal={PAL} />
                        <CountBox value={cd.m} label="Menit" pal={PAL} />
                        <CountBox value={cd.s} label="Detik" pal={PAL} />
                      </>
                    )
                  ) : <p style={{ color: PAL.dim }}>Tanggal akan diumumkan</p>}
                </div>
              </Reveal>
            </SectionBg>
          )}

          {/* ACARA */}
          <SectionBg className="px-6 py-16" testid="premium-acara" theme={theme} photo={pick(design.acara.photo, design.acara.photo_url)}>
            <Reveal>
              <h2 className="mb-8 text-center text-4xl"
                style={{ fontFamily: scriptFont, color: PAL.cream }}>
                Waktu &amp; Tempat
              </h2>
            </Reveal>
            <div className="space-y-5">
              {events.map((ev, i) => <EventCard key={i} ev={ev} delay={i * 0.12} theme={theme} />)}
            </div>
          </SectionBg>

          {/* GALERI */}
          {design.galeri.enabled && inv.gallery?.length > 0 && (
            <section className="px-6 py-16" data-testid="premium-galeri">
              <Reveal>
                <h2 className="mb-8 text-center text-4xl"
                  style={{ fontFamily: scriptFont, color: PAL.cream }}>
                  Galeri
                </h2>
              </Reveal>
              <Gallery items={inv.gallery} onOpen={setBox} />
              {design.galeri.quote && (
                <Reveal>
                  <p className="mt-8 text-center text-base italic" style={{ fontFamily: scriptFont, color: PAL.cream }}>
                    "{design.galeri.quote}"
                  </p>
                  {design.galeri.quote_source && (
                    <p className="mt-2 text-center text-[10px] uppercase tracking-[0.3em]" style={{ color: PAL.accent }}>— {design.galeri.quote_source}</p>
                  )}
                </Reveal>
              )}
            </section>
          )}

          {/* KISAH */}
          {design.kisah.enabled && design.kisah.items.some((it) => it.judul || it.cerita) && (
            <SectionBg className="px-6 py-16" testid="premium-kisah" theme={theme} photo={pick(design.kisah.photo, design.kisah.photo_url)}>
              <Reveal>
                <h2 className="mb-10 text-center text-4xl"
                  style={{ fontFamily: scriptFont, color: PAL.cream }}>
                  Love Story
                </h2>
              </Reveal>
              <div className="relative pl-5">
                <div className="absolute left-1 top-0 h-full w-px" style={{ background: `${PAL.accent}66` }} />
                {design.kisah.items.filter((it) => it.judul || it.cerita).map((it, i) => (
                  <Reveal key={i} delay={i * 0.08}>
                    <div className="relative mb-8">
                      <div className="absolute -left-4 top-2 h-2.5 w-2.5 rounded-full" style={{ background: PAL.accent }} />
                      {it.tahun && (
                        <p className="text-[10px] uppercase tracking-[0.3em]" style={{ color: PAL.accent }}>{it.tahun}</p>
                      )}
                      {it.judul && (
                        <p className="mt-1 text-xl italic" style={{ fontFamily: theme.fonts.serif, color: PAL.cream }}>{it.judul}</p>
                      )}
                      {it.cerita && <p className="mt-2 text-sm leading-relaxed" style={{ color: PAL.dim }}>{it.cerita}</p>}
                    </div>
                  </Reveal>
                ))}
              </div>
            </SectionBg>
          )}

          {/* GIFT */}
          {design.gift.enabled && design.gift.accounts.some((a) => a.nomor) && (
            <SectionBg className="px-6 py-16 text-center" testid="premium-gift" theme={theme} photo={pick(design.gift.photo, design.gift.photo_url)}>
              <Reveal>
                <h2 className="mb-4 text-4xl" style={{ fontFamily: scriptFont, color: PAL.cream }}>Wedding Gift</h2>
              </Reveal>
              {design.gift.intro && (
                <Reveal>
                  <p className="mb-8 text-sm leading-relaxed" style={{ color: PAL.dim }}>{design.gift.intro}</p>
                </Reveal>
              )}
              <div className="space-y-4 text-left">
                {design.gift.accounts.filter((a) => a.nomor).map((a, i) => (
                  <Reveal key={i} delay={i * 0.1}><GiftCard acc={a} theme={theme} /></Reveal>
                ))}
              </div>
            </SectionBg>
          )}

          {/* RSVP */}
          {design.rsvp.enabled && (
            <SectionBg className="px-6 py-16" testid="premium-rsvp" theme={theme} photo={pick(design.rsvp.photo, design.rsvp.photo_url)}>
              <Reveal>
                <h2 className="mb-6 text-center text-4xl"
                  style={{ fontFamily: scriptFont, color: PAL.cream }}>
                  Konfirmasi Kehadiran
                </h2>
              </Reveal>
              <Reveal delay={0.15}>
                <RSVPForm pid={pid} cfg={{ ...design.rsvp, cta_label: design.rsvp.cta_label }} events={events} theme={theme} />
              </Reveal>
            </SectionBg>
          )}

          {/* UCAPAN */}
          {design.ucapan.enabled && (
            <SectionBg className="px-6 py-16" testid="premium-ucapan" theme={theme} photo={pick(design.ucapan.photo, design.ucapan.photo_url)}>
              <Reveal>
                <h2 className="mb-3 text-center text-4xl"
                  style={{ fontFamily: scriptFont, color: PAL.cream }}>
                  Wishes
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mb-6 text-center text-sm" style={{ color: PAL.dim }}>
                  Berikan ucapan dan doa terbaik untuk kedua mempelai
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <Guestbook pid={pid} theme={theme} />
              </Reveal>
            </SectionBg>
          )}

          {/* PENUTUP */}
          {design.penutup.enabled && (
            <SectionBg className="px-6 py-16 text-center" testid="premium-penutup" theme={theme} photo={pick(design.penutup.photo, design.penutup.photo_url)}>
              {coverPhoto && (
                <Reveal>
                  <div className="mx-auto mb-8 h-48 w-48 overflow-hidden rounded-full"
                    style={{ boxShadow: `0 0 0 4px ${PAL.bg}, 0 0 0 5px ${PAL.accent}` }}>
                    <img src={coverPhoto} alt="" className="h-full w-full object-cover" />
                  </div>
                </Reveal>
              )}
              <Reveal>
                {design.penutup.judul && (
                  <h2 className="text-4xl" style={{ fontFamily: scriptFont, color: PAL.accent }}>{design.penutup.judul}</h2>
                )}
                {design.penutup.pesan && (
                  <p className="mt-4 text-sm leading-relaxed" style={{ color: PAL.dim }}>{design.penutup.pesan}</p>
                )}
                <DecorDivider theme={theme} w={180} />
                <p className="text-[10px] uppercase tracking-[0.3em]" style={{ color: PAL.dim }}>Kami yang berbahagia</p>
                <p className="mt-3 text-4xl italic" style={{ fontFamily: scriptFont, color: PAL.cream }}>{titleNames}</p>
                {(design.penutup.ttd_pria || design.penutup.ttd_wanita) && (
                  <div className="mt-6 grid grid-cols-2 gap-4 text-xs" style={{ color: PAL.dim }}>
                    {design.penutup.ttd_pria && (
                      <div><p className="text-[9px] uppercase tracking-wider">Keluarga Pria</p><p className="mt-1 italic" style={{ color: PAL.accent }}>{design.penutup.ttd_pria}</p></div>
                    )}
                    {design.penutup.ttd_wanita && (
                      <div><p className="text-[9px] uppercase tracking-wider">Keluarga Wanita</p><p className="mt-1 italic" style={{ color: PAL.accent }}>{design.penutup.ttd_wanita}</p></div>
                    )}
                  </div>
                )}
              </Reveal>
            </SectionBg>
          )}

          <footer className="py-8 text-center text-[10px]" style={{ color: PAL.dim }}>
            <p className="flex items-center justify-center gap-1">
              build with <Heart className="h-3 w-3" style={{ color: PAL.accent, fill: PAL.accent }} /> · Wedifylab
            </p>
          </footer>
          </div>{/* /content z-10 wrapper */}
        </div>
        </ThemeAnimCtx.Provider>
      </div>

      <AnimatePresence>{box && <Lightbox src={box} onClose={() => setBox("")} />}</AnimatePresence>
    </div>
  );
}
